import { useState, useEffect, useMemo, useCallback } from 'react';
import { API_ENDPOINTS, getDefaultHeaders } from '../utils/apiConfig';
import { toLocalDateStr } from '../utils/dateUtils';

/**
 * Hook pour gérer le calendrier équipe du dashboard
 * Gère le chargement des données, la navigation entre semaines
 * et la transformation des données pour l'affichage
 */
export const useTeamCalendar = (token) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);

  // Calcul des dates de début et fin de semaine
  const { startDate, endDate, weekNumber } = useMemo(() => {
    const now = new Date();

    // Trouver le lundi de la semaine courante
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Si dimanche, reculer de 6 jours
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff + (weekOffset * 7));
    monday.setHours(0, 0, 0, 0);

    // Dimanche de la même semaine
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    // Numéro de semaine ISO
    const tempDate = new Date(monday.getTime());
    tempDate.setHours(0, 0, 0, 0);
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));
    const week1 = new Date(tempDate.getFullYear(), 0, 4);
    const weekNum = 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);

    return {
      startDate: toLocalDateStr(monday),
      endDate: toLocalDateStr(sunday),
      weekNumber: weekNum
    };
  }, [weekOffset]);

  // Générer les jours de la semaine pour l'affichage
  const weekDays = useMemo(() => {
    const days = [];
    const start = new Date(startDate + 'T12:00:00');

    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      days.push({
        date: toLocalDateStr(date),
        dayName: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
        dayNumber: date.getDate(),
        isWeekend,
        isToday: toLocalDateStr(date) === toLocalDateStr(new Date())
      });
    }

    return days;
  }, [startDate]);

  // Charger les données
  const fetchData = useCallback(async () => {
    if (!token) {
      setError('Token manquant');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = API_ENDPOINTS.TEAM_CALENDAR(startDate, endDate);
      const response = await fetch(url, {
        headers: getDefaultHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('Erreur chargement calendrier équipe:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate]);

  // Charger les données au montage et quand les dates changent
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Navigation
  const goToPreviousWeek = useCallback(() => {
    setWeekOffset(w => w - 1);
  }, []);

  const goToNextWeek = useCallback(() => {
    setWeekOffset(w => w + 1);
  }, []);

  const goToCurrentWeek = useCallback(() => {
    setWeekOffset(0);
  }, []);

  // Rafraîchir les données
  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Vérifier si c'est la semaine courante
  const isCurrentWeek = weekOffset === 0;

  // Formater la période pour l'affichage
  const periodLabel = useMemo(() => {
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');

    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = start.toLocaleDateString('fr-FR', { month: 'short' });
    const endMonth = end.toLocaleDateString('fr-FR', { month: 'short' });
    const year = end.getFullYear();

    if (startMonth === endMonth) {
      return `${startDay} - ${endDay} ${startMonth} ${year}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  }, [startDate, endDate]);

  return {
    // Données
    data,
    loading,
    error,

    // Dates
    startDate,
    endDate,
    weekNumber,
    weekDays,
    periodLabel,
    isCurrentWeek,

    // Actions
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    refresh
  };
};

export default useTeamCalendar;
